<template>
    <div class="canvas-controls">

        <div class="zoom-group">
            <button class="control-btn" @click="handleZoomOut">
                <MinusIcon class="w-4 h-4" />
            </button>
            <span class="zoom-value">{{ Math.round(getDisplayZoom()) }}%</span>
            <button class="control-btn" @click="handleZoomIn">
                <PlusIcon class="w-4 h-4" />
            </button>
            <button class="control-btn" @click="toggleZoomLock" :class="{ active: localLayout.canvas.viewport.persistEnabled }">
                <LockIcon v-if="localLayout.canvas.viewport.persistEnabled" class="w-4 h-4" />
                <UnlockIcon v-else class="w-4 h-4" />
            </button>
            <button class="control-btn" @click="emit('resetZoom');" title="Reset zoom and center image">
                <RotateCcwIcon class="w-4 h-4" />
            </button>
        </div>

        <div class="filters-group">

            <!-- Filters toggle button -->
            <button title="Filters"
                class="control-btn"     
                :class="{ active: showFiltersPanel }"
                @click="showFiltersPanel = !showFiltersPanel">
                <SlidersIcon class="w-5 h-5" />
            </button>
            
            <!-- Shortcuts toggle button -->
            <button title="Keyboard shortcuts"
                class="control-btn"
                :class="{ active: showShortcutsPanel }"
                @click="showShortcutsPanel = !showShortcutsPanel">
                <HelpCircleIcon class="w-5 h-5" />
            </button>
            
            <!-- Filters -->
            <div v-if="showFiltersPanel" class="filters-popup"> 
                
                <div class="panel-header">
                    <h3 class="panel-title">{{ $t('Image filters') }}</h3>
                    <button class="reset-btn" @click="resetFilters" title="Reset adjustments">
                        <RotateCcwIcon class="w-4 h-4" />
                    </button>
                </div>
                
                <div class="panel-content">
                    <div class="slider-control mt-2" v-if="localLayout.dataset.type === 'MULTILABEL' || localLayout.dataset.type === 'MULTICLASS' || localLayout.dataset.type === 'imageObjectDetection'">
                        <div class="slider-header">
                            <label>{{ $t('Labels opacity') }}</label>
                            <span class="slider-value">{{ (localLayout.canvas.filters.opacity * 100).toFixed(0) }}%</span>
                        </div>
                        <Slider 
                            @update:modelValue="applyOpacity"
                            v-model="localLayout.canvas.filters.opacity" 
                            :min="0.1" 
                            :max="1"
                            :step="0.05"
                            :tooltip="false"
                            :tooltips="false"
                            class="slider-blue" />
                    </div>

                    <div class="slider-control">
                        <div class="slider-header">
                            <label>{{ $t('Brightness') }}</label>
                            <span class="slider-value">{{ Math.round(localLayout.canvas.filters.brightness * 100) }}%</span>
                        </div>
                        <Slider 
                            @update:modelValue="(value) => applyFilter('brightness', value)"
                            v-model="localLayout.canvas.filters.brightness" 
                            :min="-1" 
                            :max="1"
                            :step="0.01"
                            :tooltip="false"
                            :tooltips="false"
                            class="slider-blue" />
                    </div>

                    <div class="slider-control">
                        <div class="slider-header">
                            <label>{{ $t('Contrast') }}</label>
                            <span class="slider-value">{{ Math.round(localLayout.canvas.filters.contrast * 100) }}%</span>
                        </div>
                        <Slider 
                            @update:modelValue="(value) => applyFilter('contrast', value)"
                            v-model="localLayout.canvas.filters.contrast" 
                            :min="-1" 
                            :max="1"
                            :step="0.01"
                            :tooltip="false"
                            :tooltips="false"
                            class="slider-blue" />
                    </div>

                    <div class="slider-control">
                        <div class="slider-header">
                            <label>{{ $t('Saturation') }}</label>
                            <span class="slider-value">{{ Math.round(localLayout.canvas.filters.saturation * 100) }}%</span>
                        </div>
                        <Slider 
                            @update:modelValue="(value) => applyFilter('saturation', value)"
                            v-model="localLayout.canvas.filters.saturation" 
                            :min="-1" 
                            :max="1"
                            :step="0.01"
                            :tooltip="false"
                            :tooltips="false"
                            class="slider-blue" />
                    </div>

                    <div class="slider-control">
                        <div class="slider-header">
                            <label>{{ $t('Vibrance') }}</label>
                            <span class="slider-value">{{ Math.round(localLayout.canvas.filters.vibrance * 100) }}%</span>
                        </div>
                        <Slider 
                            @update:modelValue="(value) => applyFilter('vibrance', value)"
                            v-model="localLayout.canvas.filters.vibrance" 
                            :min="-1" 
                            :max="1"
                            :step="0.01"
                            :tooltip="false"
                            :tooltips="false"
                            class="slider-blue" />
                    </div>
                </div>

            </div>
            
            <!-- Shortcuts -->
            <div v-if="showShortcutsPanel" class="shortcuts-popup">
                
                <div class="panel-header">
                    <h3 class="panel-title">{{ $t('Keyboard shortcuts') }}</h3>
                    <button class="close-btn" @click="showShortcutsPanel = false" title="Close">
                        <XIcon class="w-4 h-4" />
                    </button>
                </div>
                
                <div class="panel-content">
                    <div class="shortcuts-list">
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Undo') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Ctrl</kbd> + <kbd>Z</kbd>
                            </div>
                        </div>
                        
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Redo') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd>
                            </div>
                        </div>
                        
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Redo (alternative)') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Ctrl</kbd> + <kbd>Y</kbd>
                            </div>
                        </div>

                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Discard new label') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Esc</kbd>
                            </div>
                        </div>
                        
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Toggle drawing/panning') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Ctrl</kbd>
                            </div>
                        </div>

                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Toggle eraser/selection') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Alt</kbd>
                            </div>
                        </div>
                        
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Straight path drawing') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Shift</kbd>
                            </div>
                        </div>
                        
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Delete selected') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Supr</kbd>
                            </div>
                        </div>
                        
                        <div class="separator-line"></div>
                        
                        <div class="shortcuts-section-title">
                            {{ $t('Smart Labeling') }}
                        </div>
                        
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Add point') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Click</kbd>
                            </div>
                        </div>
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Add area') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Click</kbd> + <kbd>Drag</kbd>
                            </div>
                        </div>
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Remove section') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Ctrl</kbd> + <kbd>Click</kbd>
                            </div>
                        </div>
                        
                        <div class="shortcut-item">
                            <span class="shortcut-desc">{{ $t('Confirm section') }}</span>
                            <div class="shortcut-keys">
                                <kbd>Tab</kbd>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

        </div>

    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { helper as $helper }    from "@/utils/helper";
import Slider                   from '@vueform/slider';

const props = defineProps({
    layout: {
        type: Object,
        required: true
    },
    canvas: {
        type: Object,
        required: true
    },
    currentBackground: {
        type: Object,
        default: () => ({})
    },
    currentObjects: {
        type: Array,
        default: () => []
    },
});

const emit = defineEmits([
    'handleZoom',
    'resetZoom',
    'updateFilters',
    'updateOpacity'
]);

const showFiltersPanel      = ref(false);
const showShortcutsPanel    = ref(false);

const localLayout           = ref(JSON.parse(JSON.stringify(props.layout)));

const layout = ref({
    opacity: 0.5,
    filters: {
        opacity:    0.5,
        brightness: 0,
        contrast:   0,
        saturation: 0,
        vibrance:   0
    }
})

const getDisplayZoom = () => {
    if (!props.layout.canvas?.viewport?.baseScale || !props.layout.canvas?.tools?.zoom) {
        return 100;
    }
    return (props.layout.canvas.tools.zoom / props.layout.canvas.viewport.baseScale) * 100;
}

const handleZoomIn = () => {
    const baseScale = props.layout.canvas.viewport.baseScale || 1;
    const currentZoom = props.layout.canvas.tools.zoom || baseScale;
    const newZoom = Math.min(baseScale * 3, currentZoom + 0.05);
    emit('handleZoom', newZoom);
}

const handleZoomOut = () => {
    const baseScale = props.layout.canvas.viewport.baseScale || 1;
    const currentZoom = props.layout.canvas.tools.zoom || baseScale;
    const newZoom = Math.max(baseScale * 0.2, currentZoom - 0.05);
    emit('handleZoom', newZoom);
}

const toggleZoomLock = () => {
    props.layout.canvas.viewport.persistEnabled = !props.layout.canvas.viewport.persistEnabled;
}

const handleClickOutside = (event) => {
    const filtersBtn        = document.querySelector('.filters-group .control-btn');
    const filtersPanel      = document.querySelector('.filters-popup');
    const shortcutsBtn      = document.querySelector('.filters-group .control-btn:nth-child(2)');
    const shortcutsPanel    = document.querySelector('.shortcuts-popup');
    
    if (showFiltersPanel.value && 
        filtersPanel && 
        filtersBtn && 
        !filtersPanel.contains(event.target) && 
        !filtersBtn.contains(event.target)) {
        showFiltersPanel.value = false;
    }
    
    if (showShortcutsPanel.value && 
        shortcutsPanel && 
        shortcutsBtn && 
        !shortcutsPanel.contains(event.target) && 
        !shortcutsBtn.contains(event.target)) {
        showShortcutsPanel.value = false;
    }
}

const resetFilters = () => {
    if (!props.currentBackground) return;

    props.currentBackground.filters = [
        new fabric.Image.filters.Brightness({ brightness: 0 }),
        new fabric.Image.filters.Contrast({ contrast: 0 }),
        new fabric.Image.filters.Saturation({ saturation: 0 }),
        new fabric.Image.filters.Vibrance({ vibrance: 0 }),
    ];

    layout.value.filters = {
        opacity:    0.5,
        brightness: 0,
        contrast:   0,
        saturation: 0,
        vibrance:   0
    };

    applyOpacity(layout.value.filters.opacity);

    try {
        props.currentBackground.applyFilters();
        props.currentBackground.canvas?.renderAll();
        emit('updateFilters', layout.value.filters);
    } catch (err) {
        console.error('Failed to reset filters:', err);
    }
}

const applyFilter = (filterType, value) => {
    if (!props.currentBackground) return;

    let imageTextureSize = props.currentBackground.width > props.currentBackground.height ? props.currentBackground.width : props.currentBackground.height;
    if (imageTextureSize > fabric.textureSize) {
        fabric.textureSize = imageTextureSize;
    }

    const filterClass = fabric.Image.filters[filterType.charAt(0).toUpperCase() + filterType.slice(1).toLowerCase()];
    if (!filterClass) return;

    const existing = props.currentBackground.filters.find(f => f[filterType] !== undefined);
    if (existing) {
        existing[filterType] = value;
    } else {
        props.currentBackground.filters.push(new filterClass({ [filterType]: value }));
    }

    layout.value.filters[filterType] = value;

    try {
        props.currentBackground.applyFilters();
        props.currentBackground.canvas?.renderAll();
        emit('updateFilters', layout.value.filters);
    } catch (err) {
        console.error('Failed applying filter:', err);
    }
}

const applyOpacity = (value) => {
    if (!props.currentObjects?.length) return;

    props.currentObjects.forEach(obj => {
        if (obj.elementType === 'bbRect') {
            obj.set({
                fill: $helper.hexToRgb(obj.color, value)
            });
            obj.setCoords();
        } else if (obj.elementType === 'mask') {
            obj.set({ opacity: value });
            obj.setCoords();
        }
    });

    layout.value.filters.opacity = value;

    props.canvas.renderAll();
    props.currentObjects[0]?.canvas?.renderAll();
    emit('updateOpacity', layout.value.filters.opacity);
}

const updateToolbarPosition = () => {
    const rightPanel = document.getElementById('editorRightPanel');
    if (!rightPanel) return;
    
    const windowWidth = window.innerWidth;
    const rightPanelWidth = rightPanel.getBoundingClientRect().width;
    const rightPanelPercentage = (rightPanelWidth / windowWidth) * 100;
    
    const canvasControls = document.querySelector('.canvas-controls');
    if (canvasControls) {
        canvasControls.style.right = `calc(${rightPanelPercentage}% + 20px)`;
    }
}

onMounted(() => {
    nextTick(() => {
        updateToolbarPosition();
        window.addEventListener('click', handleClickOutside);
        window.addEventListener('resize', updateToolbarPosition);
    });
});

onUnmounted(() => {
    window.removeEventListener('click', handleClickOutside);
    window.removeEventListener('resize', updateToolbarPosition);
});

watch(() => props.layout, (newVal) => {
    localLayout.value = JSON.parse(JSON.stringify(newVal));
}, { deep: true });
</script>

<style>
/* CANVAS CONTROLS */
.canvas-controls {
    position: fixed;
    top: 20px;
    display: flex;
    z-index: 1001;
    pointer-events: auto;
    background: #fff;
    border-radius: 8px;
    padding: 0.2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #e2e8f0;
    transition: right 0.2s ease;
}

.filters-group,
.zoom-group {
    display: flex;
    gap: 0.2rem;
    align-items: center;
}

.filters-group {
    position: relative;
    padding-left: 0.5rem;
    margin-left: 0.4rem;
}

.filters-group::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 24px;
    width: 1px;
    background-color: #94a3b8;
    opacity: 0.6;
}

.filters-group::after {
    display: none;
}

.control-btn {
    padding: 6px;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f9fafb;
    border: 1px solid transparent;
}

.control-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #e2e8f0;
}

.control-btn.active {
    background: var(--primary-50);
    color: var(--primary-600);
    border-color: var(--primary-200);
}

.zoom-value {
    font-size: 14px;
    min-width: 60px;
    text-align: center;
    color: #4b5563;
    font-weight: normal;
}

/* FILTERS POPUP */
.filters-popup,
.shortcuts-popup {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    width: 275px;
    z-index: 1002;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.filters-popup .panel-header,
.shortcuts-popup .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.filters-popup .panel-title,
.shortcuts-popup .panel-title {
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    margin: 0;
}

.filters-popup .panel-content,
.shortcuts-popup .panel-content {
    padding: 8px 12px;
}

.slider-header .slider-value {
    min-width: 36px;
    text-align: right;
}

/* PANEL HEADERS */
.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
}

/* SLIDERS */
.slider-control {
    margin-bottom: 16px;
}

.slider-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    line-height: 1.2;
}

.slider-header label, .slider-header {
    font-size: 14px;
    font-weight: 400;
    color: #4a5568;
}

.slider-value {
    font-size: 14px;
    color: #4b5563;
    font-weight: normal;
}

:deep(.slider) {
    height: 4px !important;
    background: #e5e7eb !important;
    border-radius: 9999px !important;
}

:deep(.slider-connect) {
    background: var(--primary-600, #1e4591) !important;
    border-radius: 9999px !important;
}

:deep(.slider-handle) {
    width: 16px !important;
    height: 16px !important;
    background: var(--primary-600, #1e4591) !important;
    border: 2px solid white !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
    border-radius: 9999px !important;
    top: -6px !important;
    transition: all 0.2s ease !important;
}

:deep(.slider-handle:hover) {
    background: var(--primary-700, #17356e) !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
}

/* RESET BUTTON */
.reset-btn,
.close-btn {
    padding: 6px;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f9fafb;
}

.reset-btn:hover,
.close-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
}

/* KEYBOARD SHORTCUTS STYLES */
.shortcuts-list {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
}

.shortcut-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
}

.shortcut-desc {
    font-size: 14px;
    color: #4b5563;
}

.shortcut-keys {
    display: flex;
    align-items: center;
    gap: 4px;
}

kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    padding: 2px 6px;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: #374151;
    background-color: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
}

.drag-key {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 6px;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: #374151;
    background-color: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
}

.separator-line {
    height: 1px;
    background-color: #e2e8f0;
    margin: 10px 0;
    width: 100%;
}

.shortcuts-section-title {
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    margin: 8px 0;
}
</style>